import type {
  ServerProvider,
  ServerProviderState,
  ServerProviderVersionAdvisory,
} from "@t3tools/contracts";
import { i18n } from "../../i18n";

/**
 * Visual treatment for each server-reported provider status. Centralized so
 * the default-driver card and per-instance cards share the same language.
 */
export const PROVIDER_STATUS_STYLES = {
  disabled: {
    dot: "bg-amber-400",
  },
  error: {
    dot: "bg-destructive",
  },
  incompatible: {
    dot: "bg-destructive",
  },
  ready: {
    dot: "bg-success",
  },
  warning: {
    dot: "bg-warning",
  },
} as const satisfies Record<ServerProviderState, { readonly dot: string }>;

export type ProviderStatusKey = keyof typeof PROVIDER_STATUS_STYLES;

/**
 * Derive the headline + detail copy shown under a provider's name in the
 * settings page. Prefers `provider.message` for server-supplied detail and
 * falls back to generic phrasing when the server has not yet reported any
 * state — which happens before the first probe or when an instance names a
 * driver this build does not ship.
 */
export function getProviderSummary(provider: ServerProvider | undefined) {
  if (!provider) {
    return {
      headline: i18n.t("provider.checkingStatus"),
      detail: i18n.t("provider.waitingStatus"),
    };
  }
  if (!provider.enabled) {
    return {
      headline: i18n.t("provider.disabled"),
      detail: provider.message ?? i18n.t("provider.disabledDescription"),
    };
  }
  if (!provider.installed) {
    return {
      headline: i18n.t("provider.notFound"),
      detail: provider.message ?? i18n.t("provider.cliNotFound"),
    };
  }
  if (provider.status === "incompatible") {
    return {
      headline: i18n.t("provider.incompatible"),
      detail: provider.message ?? i18n.t("provider.incompatibleDescription"),
    };
  }
  if (provider.auth.status === "authenticated") {
    const authLabel = provider.auth.label ?? provider.auth.type;
    return {
      headline: authLabel
        ? `${i18n.t("provider.authenticated")} · ${authLabel}`
        : i18n.t("provider.authenticated"),
      detail: provider.message ?? null,
    };
  }
  if (provider.auth.status === "unauthenticated") {
    return {
      headline: i18n.t("provider.notAuthenticated"),
      detail: provider.message ?? null,
    };
  }
  if (provider.status === "warning") {
    return {
      headline: i18n.t("provider.needsAttention"),
      detail: provider.message ?? i18n.t("provider.couldNotVerify"),
    };
  }
  if (provider.status === "error") {
    return {
      headline: i18n.t("provider.unavailable"),
      detail: provider.message ?? i18n.t("provider.startupCheckFailed"),
    };
  }
  return {
    headline: i18n.t("provider.available"),
    detail: provider.message ?? i18n.t("provider.authCouldNotVerify"),
  };
}

/**
 * Normalize a version string for display. Adds the `v` prefix when the
 * driver reported a bare version (e.g. `1.2.3`) so cards render
 * consistently regardless of driver.
 */
export function getProviderVersionLabel(version: string | null | undefined) {
  if (!version) return null;
  return version.startsWith("v") ? version : `v${version}`;
}

export function getProviderVersionAdvisoryPresentation(
  advisory: ServerProviderVersionAdvisory | undefined,
): {
  readonly detail: string;
  readonly updateCommand: string | null;
  readonly emphasis: "normal" | "strong";
} | null {
  if (!advisory || advisory.status === "current" || advisory.status === "unknown") {
    return null;
  }

  const version = advisory.latestVersion;
  const versionLabel = getProviderVersionLabel(version);

  return {
    detail:
      advisory.message ??
      (versionLabel
        ? i18n.t("provider.updateAvailableInstall", { version: versionLabel })
        : i18n.t("provider.updateAvailableLatest")),
    updateCommand: advisory.updateCommand,
    emphasis: "normal" as const,
  };
}
